import type { LogoSettings } from '@/lib/types';

interface BrandLogoProps {
  url?: string | null;
  name?: string;
  settings?: LogoSettings | null;
  className?: string;
}

function initialsOf(name?: string): string {
  const clean = (name || 'TGMCI').replace(/[^A-Za-z ]/g, ' ').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const letters = parts.map((w) => w[0]).join('').slice(0, 3);
  return (letters || 'TG').toUpperCase();
}

/**
 * Renders the church logo with admin-controlled size, background, padding and
 * shape. When no logo is uploaded it shows a clean monogram so no generated
 * image is ever used.
 */
export default function BrandLogo({ url, name, settings, className = '' }: BrandLogoProps) {
  const size = settings?.size ?? 40;
  const padding = settings?.padding ?? 0;
  const useBackground = settings?.useBackground ?? false;
  const background = settings?.background || '';
  const shape = settings?.shape ?? 'circle';

  const radius = shape === 'circle' ? '9999px' : shape === 'rounded' ? '12px' : '6px';
  const showBackground = useBackground && background.trim() !== '';

  return (
    <div
      className={`flex items-center justify-center shrink-0 ${!url ? 'bg-primary-600' : ''} ${className}`}
      style={{
        width: size,
        height: size,
        padding: showBackground ? padding : 0,
        borderRadius: radius,
        backgroundColor: showBackground ? background : undefined,
        boxSizing: 'border-box',
      }}
    >
      {url ? (
        <img
          src={url}
          alt={`${name || 'Church'} logo`}
          style={{ width: '100%', height: '100%', borderRadius: radius, objectFit: 'cover' }}
        />
      ) : (
        <span className="font-heading font-bold text-white" style={{ fontSize: Math.max(10, size * 0.3) }}>
          {initialsOf(name)}
        </span>
      )}
    </div>
  );
}